/**
 * Tasks hook with TanStack Query — mirrors useDocuments.ts's shape.
 *
 * The old Tasks feature re-fetches the whole task list after a successful upload
 * rather than optimistically patching one task (that's what actually moves a task
 * from Open to In Progress, and the API decides the bucket), so this hook does the
 * same via `invalidateQueries` rather than a cache-write — once per batch, through
 * `refreshAfterUpload`.
 *
 * A task upload also creates a loan document, so it invalidates both document
 * lists over the same Vesta data: the Docs page's BP surface and the wizard
 * step's own.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taskService } from '@/services/tasks';
import { docsService } from '@/services/docs';
import { contentTypeFromFileName, downloadFileName } from '@/types/documents';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { applicationKeys } from '@/services/wizard';
import { docsQueryKey, tasksQueryKey } from '@/hooks/loanQueryKeys';
import type { TaskAttachment } from '@/types/tasks';

export const taskAttachmentQueryKey = (
  loanId: string,
  documentId: string,
  fileId: string,
  variant: 'file' | 'thumbnail' = 'file',
) => ['loan', loanId, 'tasks', 'attachment', documentId, fileId, variant] as const;

/** Auth and not-found failures won't self-resolve — retrying them only delays the error state. */
function retryUnlessTerminal(failureCount: number, error: Error) {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if (status === 401 || status === 403 || status === 404) return false;
  }
  return failureCount < 1;
}

export function useTasks(loanId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = tasksQueryKey(loanId ?? '');

  const tasksQuery = useQuery({
    queryKey,
    queryFn: () => taskService.getTasks(loanId as string),
    enabled: !!loanId,
    staleTime: 30_000,
    retry: retryUnlessTerminal,
    throwOnError: false,
  });

  const uploadMutation = useMutation({
    mutationFn: async (input: { taskId: string; category: string; file: File }) => {
      if (!loanId) throw new Error('Loan ID is required to upload a task document.');
      return taskService.uploadAttachment(loanId, input.taskId, {
        documentType: input.category,
        file: input.file,
      });
    },
  });

  // Called once a whole batch has settled, never per file: the first file of a batch
  // can move its task out of Open, and re-reading then unmounts the row — and the
  // upload list — while the rest of the batch is still going up.
  const refreshAfterUpload = () => {
    queryClient.invalidateQueries({ queryKey });
    if (!loanId) return;
    queryClient.invalidateQueries({ queryKey: docsQueryKey(loanId) });
    queryClient.invalidateQueries({ queryKey: applicationKeys.documents(loanId) });
  };

  return {
    tasks: tasksQuery.data,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    refetch: tasksQuery.refetch,
    upload: uploadMutation,
    refreshAfterUpload,
  };
}

/** Carries no information — "unknown", not "binary". */
const GENERIC_CONTENT_TYPES = new Set(['application/octet-stream', 'binary/octet-stream']);

/** The served content type, or one derived from the file name when it says nothing. */
function usableContentType(served: string | null | undefined, fileName: string): string {
  // Parameters and casing are both legal on the wire ("image/jpeg; charset=binary").
  const bare = (served ?? '').split(';')[0].trim().toLowerCase();
  return bare && !GENERIC_CONTENT_TYPES.has(bare) ? bare : contentTypeFromFileName(fileName);
}

/**
 * The bytes behind one task attachment, as an object URL the card can put in an
 * `<img>`, an `<iframe>` and a `target="_blank"` link alike.
 *
 * The list read carries no URL and no usable MIME type — its `type` is the Vesta
 * document type name, not a content type — so the bytes come from the download
 * and the content type from the download or, failing that, the file name.
 * `enabled` is the owning task row's expanded state: nothing is fetched until the
 * borrower opens the task that holds the file.
 *
 * An object URL rather than a `data:` one because the card's "New Tab" link is a
 * top-level navigation, and browsers block those to `data:` URLs.
 */
export function useTaskAttachmentFile(
  loanId: string | undefined,
  attachment: TaskAttachment,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const canFetch = enabled && !!loanId && !!attachment.documentId;

  // Shared with `fetchFile` below, so an on-demand download and the preview hit
  // the same cache entry rather than pulling the same bytes twice.
  const fileQueryOptions = {
    queryKey: taskAttachmentQueryKey(loanId ?? '', attachment.documentId, attachment.id),
    // Signal forwarded so collapsing the row cancels a large download in flight
    // rather than paying for bytes nobody is waiting for (Copilot, PR #286).
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      docsService.downloadFile(
        loanId as string,
        {
          documentId: attachment.documentId,
          // Omitted rather than sent empty when the wire carries no file id: the
          // API binds this as a Guid and falls back to the document's first file
          // when it is absent, so `''` would 400 where absence succeeds.
          ...(attachment.id ? { attachmentId: attachment.id } : {}),
        },
        { signal },
      ),
    // A borrower's own uploaded file doesn't change under them; re-downloading it
    // every time the task row is collapsed and reopened is pure transfer cost.
    staleTime: 5 * 60_000,
    retry: retryUnlessTerminal,
  };

  const fileQuery = useQuery({
    ...fileQueryOptions,
    enabled: canFetch,
    throwOnError: false,
  });

  /**
   * The bytes on demand, for a Download control that has to work whether or not
   * Preview was ever opened — `enabled` keeps the query idle until it is, so a
   * download that waited on `url` could only ever render dead. Resolves straight
   * from the cache once Preview (or an earlier download) has pulled the file.
   */
  const fetchFile = async () => {
    if (!loanId || !attachment.documentId) throw new Error('This document has no file to download.');
    const data = await queryClient.ensureQueryData(fileQueryOptions);
    // Same empty-200 the preview handles below: the API serialises a missing or
    // purged blob as an empty string rather than failing.
    if (!data.Content) throw new Error('This file has no content to download.');
    const type = usableContentType(data.Type, attachment.title);
    // The API's own `Name`, not the list's: same Vesta field today, but the
    // download response is the one that travels with these bytes.
    return { content: data.Content, contentType: type, fileName: downloadFileName(data.Name ?? attachment.title, type) };
  };

  const contentType = usableContentType(fileQuery.data?.Type, attachment.title);
  const isPdf = contentType === 'application/pdf';

  /**
   * The card's preview image for a PDF.
   *
   * Vesta normalises uploaded photos to PDF on ingest — a borrower's `id.jpg`
   * comes back as `id.jpg.pdf` — so this is the common case for a task
   * attachment, not an edge one. The API rasterises page 1 to a PNG, and answers
   * `Content: null` only for a file it cannot render, which lands on the card's
   * file-icon fallback. Only fired for a PDF — for anything else the full download
   * already IS the image.
   */
  const thumbnailQuery = useQuery({
    queryKey: taskAttachmentQueryKey(loanId ?? '', attachment.documentId, attachment.id, 'thumbnail'),
    queryFn: ({ signal }) =>
      docsService.downloadFile(
        loanId as string,
        {
          documentId: attachment.documentId,
          ...(attachment.id ? { attachmentId: attachment.id } : {}),
          isThumbnail: true,
        },
        { signal },
      ),
    enabled: canFetch && isPdf,
    staleTime: 5 * 60_000,
    retry: retryUnlessTerminal,
    throwOnError: false,
  });

  const url = useObjectUrl(fileQuery.data?.Content, contentType);
  const thumbnailUrl = useObjectUrl(
    thumbnailQuery.data?.Content,
    usableContentType(thumbnailQuery.data?.Type, thumbnailQuery.data?.Name ?? ''),
  );

  // A 200 carrying no bytes is reachable on the plain download: the API's
  // `WizardDocumentService.DownloadAsync` returns `download.Content ?? []`, so a
  // missing or purged blob serialises as an empty string instead of failing.
  // Left unhandled the card sits with both buttons disabled and nothing saying
  // why, since the error line only renders on a thrown error. The THUMBNAIL is
  // deliberately exempt — `Content: null` is its documented answer for a PDF
  // until the API can rasterise one, and that is the file-icon case, not an error.
  const servedNoContent = fileQuery.isSuccess && !fileQuery.data?.Content;

  // Whether anything that could still produce `previewUrl` is in flight. Not the
  // same as `isLoading`: for a PDF the picture comes from the thumbnail request,
  // which cannot even start until the download reveals the content type, so the
  // download's own state goes false while there is still nothing to paint.
  // `canFetch` keeps a collapsed row — mounted but not fetching, since
  // CollapsibleContent only hides its children — out of the loading state.
  const isPreviewLoading =
    fileQuery.isLoading || (isPdf && canFetch && !thumbnailQuery.isSuccess && !thumbnailQuery.isError);

  return {
    /** The file itself — the dialog preview and the "New Tab" link. */
    url,
    /** Pulls the bytes on demand, for a Download the preview never gated. */
    fetchFile,
    /** What to save the file as — the stored name can lie about what the bytes are. */
    fileName: downloadFileName(fileQuery.data?.Name ?? attachment.title, contentType),
    /** What the card paints, when there is something to paint. */
    previewUrl: thumbnailUrl ?? (contentType.startsWith('image/') ? url : undefined),
    contentType,
    isLoading: fileQuery.isLoading,
    /** Lets the card tell "still arriving" apart from "there is no preview". */
    isPreviewLoading,
    error: fileQuery.error ?? (servedNoContent ? new Error('This file has no content to show.') : null),
  };
}

